<script setup lang="ts">
import {
  MoreHorizontal,
  FlipHorizontal,
  RotateCw,
  Download,
  Replace,
  Sparkles,
  Scissors,
  Eraser,
  StretchHorizontal,
  Wand2,
  CircleDashed,
  Blend,
  PanelTop,
} from 'lucide-vue-next';

const rows = [
  {
    key: 'replace',
    label: 'Replace Image',
    desc: 'Swap in another image from upload, search, recent assets, or AI tools.',
  },
  {
    key: 'generate',
    label: 'AI Generate',
    desc: 'Generate a fresh image from a prompt, style, and model.',
  },
  {
    key: 'bgRemover',
    label: 'Remove Background',
    desc: 'Cut out the main subject and remove the background.',
  },
  {
    key: 'imageUpscale',
    label: 'Upscale',
    desc: 'Make blurry images sharper with AI.',
  },
  {
    key: 'vectorize',
    label: 'Vectorize',
    desc: 'Convert an image into vector-style output for cleaner scaling.',
  },
  {
    key: 'removeObjects',
    label: 'Remove Objects',
    desc: 'Brush over unwanted areas to clean them up.',
  },
  {
    key: 'crop',
    label: 'Crop',
    desc: 'Crop the image to keep only the part you want.',
  },
  { key: 'separator' },
  { key: 'flip', label: 'Flip', desc: 'Flip the image horizontally or vertically.' },
  { key: 'rotate', label: 'Rotate 90°', desc: 'Rotate the image in 90-degree steps.' },
  { key: 'stroke', label: 'Stroke', desc: 'Add a border around the image.' },
  { key: 'shadow', label: 'Shadow', desc: 'Apply image shadow presets or fine-tune manually.' },
  { key: 'radius', label: 'Corner Radius', desc: 'Adjust the four image corners.' },
  { key: 'opacity', label: 'Opacity', desc: 'Control overall image transparency.' },
  { key: 'export', label: 'Export Image', desc: 'Export only this selected image.' },
  {
    key: 'more',
    label: 'More',
    desc: 'Open advanced image options like fit mode, blend mode, clip path, and skew.',
  },
];
</script>

<template>
  <div class="doc-table-image-toolbar-attrs my-4 min-w-0 overflow-x-auto">
    <table class="doc-it w-full border-collapse text-[0.875rem] leading-[1.5]">
      <thead>
        <tr>
          <th class="doc-it-th doc-it-col1">Control</th>
          <th class="doc-it-th doc-it-col2">Name</th>
          <th class="doc-it-th doc-it-col3">What it does</th>
        </tr>
      </thead>
      <tbody>
        <template v-for="row in rows" :key="row.key">
          <tr v-if="row.key === 'separator'" class="doc-it-sep">
            <td colspan="3">
              <div class="doc-it-sep-line"></div>
            </td>
          </tr>
          <tr v-else>
            <td class="doc-it-td doc-it-col1">
              <div class="doc-it-s">
                <Replace
                  v-if="row.key === 'replace'"
                  class="w-5 h-5 text-black/70 dark:text-white/70"
                />
                <Sparkles
                  v-else-if="row.key === 'generate'"
                  class="w-5 h-5 text-black/70 dark:text-white/70"
                />
                <Eraser
                  v-else-if="row.key === 'bgRemover'"
                  class="w-5 h-5 text-black/70 dark:text-white/70"
                />
                <StretchHorizontal
                  v-else-if="row.key === 'imageUpscale'"
                  class="w-5 h-5 text-black/70 dark:text-white/70"
                />
                <Wand2
                  v-else-if="row.key === 'vectorize'"
                  class="w-5 h-5 text-black/70 dark:text-white/70"
                />
                <Scissors
                  v-else-if="row.key === 'removeObjects'"
                  class="w-5 h-5 text-black/70 dark:text-white/70"
                />
                <Scissors
                  v-else-if="row.key === 'crop'"
                  class="w-5 h-5 text-black/70 dark:text-white/70"
                />
                <FlipHorizontal
                  v-else-if="row.key === 'flip'"
                  class="w-5 h-5 text-black/70 dark:text-white/70"
                />
                <RotateCw
                  v-else-if="row.key === 'rotate'"
                  class="w-5 h-5 text-black/70 dark:text-white/70"
                />
                <PanelTop
                  v-else-if="row.key === 'stroke'"
                  class="w-5 h-5 text-black/70 dark:text-white/70"
                />
                <PanelTop
                  v-else-if="row.key === 'shadow'"
                  class="w-5 h-5 text-black/70 dark:text-white/70"
                />
                <CircleDashed
                  v-else-if="row.key === 'radius'"
                  class="w-5 h-5 text-black/70 dark:text-white/70"
                />
                <Blend
                  v-else-if="row.key === 'opacity'"
                  class="w-5 h-5 text-black/70 dark:text-white/70"
                />
                <Download
                  v-else-if="row.key === 'export'"
                  class="w-5 h-5 text-black/70 dark:text-white/70"
                  :stroke-width="1.5"
                />
                <span v-else-if="row.key === 'more'" class="doc-it-more">
                  <MoreHorizontal class="w-4 h-4 text-black/50 dark:text-white/50" />
                </span>
              </div>
            </td>
            <td class="doc-it-td doc-it-col2">{{ row.label }}</td>
            <td class="doc-it-td doc-it-col3">{{ row.desc }}</td>
          </tr>
        </template>
      </tbody>
    </table>
  </div>
</template>

<style scoped>
.doc-it {
  table-layout: fixed;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 12px;
  overflow: hidden;
}

.dark .doc-it {
  border-color: rgba(255, 255, 255, 0.08);
}

.doc-it-th,
.doc-it-td {
  padding: 0.55rem 0.7rem;
  text-align: left;
  vertical-align: middle;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  word-break: break-word;
  overflow-wrap: anywhere;
}

.dark .doc-it-th,
.dark .doc-it-td {
  border-bottom-color: rgba(255, 255, 255, 0.06);
}

.doc-it-th {
  background: rgba(0, 0, 0, 0.03);
  font-weight: 600;
  font-size: 0.84rem;
  color: rgba(0, 0, 0, 0.55);
  letter-spacing: 0.01em;
}

.dark .doc-it-th {
  background: rgba(255, 255, 255, 0.04);
  color: rgba(255, 255, 255, 0.5);
}

.doc-table-image-toolbar-attrs .doc-it th:nth-child(1),
.doc-table-image-toolbar-attrs .doc-it td:nth-child(1),
.doc-it-col1 {
  width: 15% !important;
}

.doc-table-image-toolbar-attrs .doc-it th:nth-child(2),
.doc-table-image-toolbar-attrs .doc-it td:nth-child(2),
.doc-it-col2 {
  width: 15% !important;
}

.doc-table-image-toolbar-attrs .doc-it th:nth-child(3),
.doc-table-image-toolbar-attrs .doc-it td:nth-child(3),
.doc-it-col3 {
  width: 70% !important;
}

tbody tr:last-child td {
  border-bottom: 0;
}

.doc-it-s {
  pointer-events: none;
  user-select: none;
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
}

.doc-it-more {
  display: inline-flex;
  height: 2rem;
  align-items: center;
  justify-content: center;
  border-radius: 0.75rem;
  border: 1px solid rgba(0, 0, 0, 0.12);
  padding: 0 0.5rem;
}

.dark .doc-it-more {
  border-color: rgba(255, 255, 255, 0.14);
}

tr:has(+ .doc-it-sep) td {
  border-bottom: 0 !important;
}

.doc-it-sep td {
  padding: 0 !important;
  border-bottom: 0 !important;
}

.doc-it-sep-line {
  height: 1px;
  background: rgba(0, 0, 0, 0.05);
  margin: 0;
}

.dark .doc-it-sep-line {
  background: rgba(255, 255, 255, 0.06);
}
</style>
